import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ErrorMessage, Form, Formik } from 'formik';
import {
  success as renderSuccess,
  error as renderError,
} from 'helpers/toaster.js';
import BSButton from 'Components/Button/BSButton';
import FormikTextField from 'Components/Form/FormikTextField';
import Drawer from 'Components/Drawer/Drawer';
import Loader from 'Components/Loader/Loader';
import Styles from './ProjectDrawer.module.scss';
import {
  CONTENT_TYPE_OPTIONS,
  CAMPAIGN_GOALS,
} from '../../constants/contentTypes';
import { LANGUAGE_OPTIONS } from '../../constants/languages';
import Dropdown from 'Components/Dropdown/Dropdown';
import FormikDateField from 'Components/Form/FormikDateField';
import { manualProjectCreateSchema } from 'schemas/projectSchemas';
import useCreateProject from 'hooks/projects/useCreateProject';
import useUpdateProject from 'hooks/projects/useUpdateProject';
import ProgressBar from 'react-bootstrap/ProgressBar';
import AiPoweredGeneratedTabs from './ProjectTile/AiPoweredGeneratedTabs/AiPoweredGeneratedTabs';
import { ReactComponent as Instagram } from '../../assets/svg/instagram.svg';
import { ReactComponent as Other } from '../../assets/svg/Group.svg';
import { ReactComponent as X } from '../../assets/svg/x.svg';
import { ReactComponent as Linkedin } from '../../assets/svg/linkedin.svg';
import { ReactComponent as Youtube } from '../../assets/svg/youtube.svg';
import { ReactComponent as Blog } from '../../assets/svg/blog.svg';
import { ReactComponent as CaseStudy } from '../../assets/svg/case-study.svg';
import { ReactComponent as Guide } from '../../assets/svg/Guide.svg';
import { ReactComponent as Quiz } from '../../assets/svg/quiz.svg';
import { ReactComponent as Facebook } from '../../assets/svg/facebook.svg';
import { ReactComponent as TikTok } from '../../assets/svg/tik-tok.svg';
import { ReactComponent as EmailNewsletter } from '../../assets/svg/email-newsletter.svg';

import Tag from 'Components/tag/Tag';
import { useNavigate } from 'react-router-dom';
import CampaignErrorModal from 'Components/Modal/CampaignErrorModal/CampaignErrorModal';
import onBoardingService from 'services/onBoardingService';
import SupportingDocumentsUpload from 'Components/SupportingDocumentsUpload';
import { extractSupportingDocuments } from 'services/projectService';

// Function to detect language from campaign description (for backward compatibility with old campaigns)
const detectLanguageFromDescription = (description) => {
  if (!description) return 'en-US'; // Default to English (US) if no description

  // First check for explicit language instruction
  const languageInstructionRegex = /Please ensure the entire output is in ([^\.]+)\./i;
  const britishEnglishRegex = /Please ensure the entire output uses British English/i;
  
  // Check for British English instruction first
  if (britishEnglishRegex.test(description)) {
    return 'en-GB';
  }
  
  // Then check for other language instructions
  const languageMatch = description.match(languageInstructionRegex);
  if (languageMatch && languageMatch[1]) {
    const requestedLanguage = languageMatch[1].trim();
    
    // Map the language name to language code
    const languageMap = LANGUAGE_OPTIONS.reduce((map, option) => {
      map[option.label.toLowerCase()] = option.value;
      return map;
    }, {});
    
    // Try to find an exact match first
    if (languageMap[requestedLanguage.toLowerCase()]) {
      return languageMap[requestedLanguage.toLowerCase()];
    }
    
    // If no exact match, try to find a partial match
    for (const [langName, langCode] of Object.entries(languageMap)) {
      if (requestedLanguage.toLowerCase().includes(langName) || 
          langName.includes(requestedLanguage.toLowerCase())) {
        return langCode;
      }
    }
  }
  
  // If no explicit instruction, try to detect language from content
  // This is a simplistic approach - for production use, consider using a proper
  // language detection library or API
  
  // Check for common language markers
  const languageMarkers = [
    { markers: ['ñ', 'ó', 'á', 'é', 'í', '¿', '¡'], lang: 'es' },
    { markers: ['ç', 'é', 'è', 'ê', 'à', 'â', 'ô'], lang: 'fr' },
    { markers: ['ä', 'ö', 'ü', 'ß'], lang: 'de' },
    { markers: ['à', 'è', 'é', 'ì', 'ò', 'ù'], lang: 'it' },
    { markers: ['ã', 'õ', 'á', 'é', 'í', 'ó', 'ú', 'ç'], lang: 'pt' },
    { markers: ['й', 'ц', 'у', 'к', 'е', 'н', 'г', 'ш', 'щ', 'з', 'х', 'ъ'], lang: 'ru' },
    { markers: ['ä', 'ö', 'ü', 'õ'], lang: 'et' },
  ];
  
  for (const { markers, lang } of languageMarkers) {
    if (markers.some(marker => description.includes(marker))) {
      return lang;
    }
  }
  
  // Common English (UK) spellings vs US spellings
  const ukSpellings = ['colour', 'favourite', 'centre', 'theatre', 'analyse', 
                       'organise', 'realise', 'specialise', 'travelled', 'cancelled'];
  
  if (ukSpellings.some(word => 
      new RegExp(`\\b${word}\\b`, 'i').test(description))) {
    return 'en-GB';
  }
  
  // Default to US English if no other language detected
  return 'en-US';
};

// Function to remove language instructions from description
const removeLanguageInstructions = (description) => {
  if (!description) return '';
  
  // Remove language instructions
  let cleanedDescription = description
    .replace(/\n\nPlease ensure the entire output is in [^\.]+\./g, '')
    .replace(/\n\nPlease ensure the entire output uses British English spelling and terminology\./g, '');
  
  // Trim any extra whitespace that might have been left
  return cleanedDescription.trim();
};

// Function to get business details by ID
const getBusinessById = async (brandId) => {
  try {
    return await onBoardingService.getBusinessById(brandId);
  } catch (error) {
    console.error('Error fetching business details:', error);
    return null;
  }
};

const ProjectDrawer = ({ show, onHide, action, project }) => {
  const [errorModal, setErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState({ message: '', link: '' });
  const [isLoadingBrandDetails, setIsLoadingBrandDetails] = useState(false);

  // Supporting documents state
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [extractedSupportingContent, setExtractedSupportingContent] =
    useState('');
  const [isExtractingContent, setIsExtractingContent] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [supportingDocsErrors, setSupportingDocsErrors] = useState([]);

  const navigate = useNavigate();
  const isEdit = action !== 'create';
  const { user } = useSelector((state) => state.auth);
  const { subscriptions } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};

  const { mutateAsync: createProject, isLoading: isCreateLoading } =
    useCreateProject({
      userId: user?.id,
      showError: (error) => {
        const appSumoPlans = [
          'AppSumo Tier1',
          'AppSumo Tier2',
          'AppSumo Tier3',
        ];

        if (appSumoPlans.includes(currentSubscription?.plan_name)) {
          setErrorDetails({
            message: error?.response?.data?.error || 'Something went wrong',
            link: 'https://appsumo.com/products/vocable-ai',
          });
          setErrorModal(true);
        } else {
          renderError(error);
        }
      },
    });

  const { mutateAsync: updateProject, isLoading: isUpdateLoading } =
    useUpdateProject();

  const defaultType = 'ai_powered';
  const [tagState, setTagState] = useState([
    {
      id: 'linkedin',
      title: 'Linkedin Post',
      selected: false,
      icon: <Linkedin />,
    },
    {
      id: 'linkedin-article',
      title: 'Linkedin Article',
      selected: false,
      icon: <Linkedin />,
    },

    {
      id: 'facebook-post',
      title: 'Facebook Post',
      selected: false,
      icon: <Facebook />,
    },
    {
      id: 'instagram',
      title: 'Instagram Carousel',
      selected: false,
      icon: <Instagram />,
    },
    {
      id: 'tiktok-video',
      title: 'TikTok Video',
      selected: false,
      icon: <TikTok />,
    },
    {
      id: 'instagram-post',
      title: 'Instagram Post',
      selected: false,
      icon: <Instagram />,
    },
    {
      id: 'instagram-reel',
      title: 'Instagram Reel',
      selected: false,
      icon: <Instagram />,
    },
    {
      id: 'medium',
      title: 'Medium Article',
      selected: false,
      icon: <Other />,
    },
    {
      id: 'twitter',
      title: 'Twitter Thread',
      selected: false,
      icon: <X />,
    },
    {
      id: 'youtube',
      title: 'Youtube Script',
      selected: false,
      icon: <Youtube />,
    },
    {
      id: 'email-newsletter',
      title: 'Email Newsletter',
      selected: false,
      icon: <EmailNewsletter />,
    },
    {
      id: 'blog',
      title: 'Blog Post',
      selected: false,
      icon: <Blog />,
    },
    {
      id: 'case',
      title: 'Case Study',
      selected: false,
      icon: <CaseStudy />,
    },
    {
      id: 'guides',
      title: 'Guide',
      selected: false,
      icon: <Guide />,
    },
    {
      id: 'quiz',
      title: 'Quiz',
      selected: false,
      icon: <Quiz />,
    },
  ]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [creationType, setCreationType] = useState(
    project?.creation_type || defaultType,
  );
  const contentNo = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '25', value: 25 },
  ];

  useEffect(() => {
    if (project?.content_types) {
      const selectedContentTypes = project.content_types.split(', ');
      setTagState((prevTags) =>
        prevTags.map((tag) => ({
          ...tag,
          selected: selectedContentTypes.includes(tag.title),
        })),
      );
    }
  }, [project]);

  // Handle supporting documents extraction
  useEffect(() => {
    const handleExtractSupportingContent = async () => {
      if (supportingFiles.length === 0) {
        setExtractedSupportingContent('');
        return;
      }

      setIsExtractingContent(true);
      setExtractionProgress(0);
      setSupportingDocsErrors([]);

      try {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setExtractionProgress((prev) => Math.min(prev + 10, 90));
        }, 200);

        const response = await extractSupportingDocuments(supportingFiles);

        clearInterval(progressInterval);
        setExtractionProgress(100);

        if (response?.data?.data) {
          setExtractedSupportingContent(response.data.data);
          if (response.data.errors && response.data.errors.length > 0) {
            setSupportingDocsErrors(
              response.data.errors.map((e) => `${e.filename}: ${e.error}`),
            );
          }
        }
      } catch (error) {
        console.error('Error extracting content:', error);
        setSupportingDocsErrors(['Failed to extract content from files']);
      } finally {
        setIsExtractingContent(false);
      }
    };

    handleExtractSupportingContent();
  }, [supportingFiles]);

  // Effect to fetch brand details when editing a project
  useEffect(() => {
    if (isEdit && project?.business_id) {
      const fetchBrandDetails = async () => {
        try {
          setIsLoadingBrandDetails(true);
          const response = await getBusinessById(project.business_id);
          
          if (response && response.data) {
            // Get the summary from either auto-generated or user-entered
            const summary = response.data.auto_generated_summary || response.data.user_entered_summary;
            
            if (summary && summary.target_audience && summary.target_audience.length > 0) {
              // Format the target audience as a comma-separated string
              const audienceString = summary.target_audience.join(', ');
              setFieldValue('target_audience', audienceString);
            }
          }
        } catch (error) {
          console.error('Error fetching brand details:', error);
        } finally {
          setIsLoadingBrandDetails(false);
        }
      };

      fetchBrandDetails();
    }
  }, [isEdit, project?.business_id]);

  const initialValues = useMemo(() => {
    // Get the raw description
    const rawDescription = project?.description || '';

    // Clean the description for display by removing language instructions
    const cleanedDescription = removeLanguageInstructions(rawDescription);

    // Detect language from the raw description
    const detectedLanguage = rawDescription
      ? detectLanguageFromDescription(rawDescription)
      : 'en-US';

    return {
      name: project?.name || '',
      description: cleanedDescription, // Use cleaned description without language instructions
      content_types: project?.content_types
        ? project?.content_types
            .split(',')
            .map((val) => ({ label: val, value: val }))
        : [],
      campaign_goal: project?.campaign_goal || '',
      brand: project?.business_id || '',
      content_no: project?.content_no || '', // Start empty to allow optional input
      // Use the detected language or fallback to project.language if available
      language: project?.language || detectedLanguage,
      target_audience: project?.target_audience || '',
      key_message: project?.key_message || '',
      start_date: project?.start_date
        ? new Date(project?.start_date)
        : new Date(),
      end_date: project?.end_date ? new Date(project?.end_date) : new Date(),
    };
  }, [project]);

  const modalTitle =
    action === 'create' ? 'Create New Campaign' : 'Edit Campaign';

  const getSelectedTagsString = (tags) => {
    return tags
      .filter((tag) => tag.selected)
      .map((tag) => tag.title)
      .join(', ');
  };

  function transformBrandsData(brandsData) {
    return brandsData?.map((item) => {
      const [key, value] = Object.entries(item)[0];
      if (value === null) {
        return { label: 'Unknown', value: key };
      }
      // value is an object with brand data, extract the brand_name
      return { label: value.brand_name || 'Unknown', value: key };
    });
  }

  // Prepare campaign data from form values
  const prepareCampaignData = (values) => {
    const selectedTagsString = getSelectedTagsString(tagState);
    const defaultBriefCount = 30;
    const briefs_qtt = values.content_no || defaultBriefCount;
    const amount = values.content_no || defaultBriefCount;

    const data = {
      ...values,
      description: values.description,
      business_id: values?.brand,
      campaign_goal: values.campaign_goal,
      content_types: selectedTagsString,
      creation_type: creationType,
      language: values.language,
      briefs_qtt,
      amount,
      supporting_context: extractedSupportingContent || '',
    };

    return { data, briefs_qtt, selectedTagsString };
  };

  // Handle Save Only (edit mode) - just update campaign fields, no regeneration
  const handleSaveOnly = async (values) => {
    const { data } = prepareCampaignData(values);

    const payload = {
      body: data,
      projectId: project?.id,
    };

    try {
      const result = await updateProject(payload);
      onHide();
      renderSuccess('Campaign updated successfully!');
      // Stay on current page - no navigation to regenerate
    } catch (error) {
      // Error is handled by the showError callback in useUpdateProject
    }
  };

  // Handle Save and Regenerate (edit mode) - update campaign AND regenerate all briefs
  const handleSaveAndRegenerate = async (values) => {
    const { data, briefs_qtt, selectedTagsString } = prepareCampaignData(values);

    const payload = {
      body: data,
      projectId: project?.id,
    };

    try {
      const result = await updateProject(payload);
      onHide();
      renderSuccess('Campaign updated! Regenerating briefs...');
      navigate(
        `/projects/${result?.data?.project?.id}/newPlan?isManual=false`,
        {
          state: {
            project: result?.data?.project,
            campaignCards: true,
            amount: briefs_qtt,
            customAmount: true,
            content_types: selectedTagsString,
            replaceExisting: true, // Flag to replace all existing briefs
          },
        },
      );
    } catch (error) {
      // Error is handled by the showError callback in useUpdateProject
    }
  };

  // Handle Create (new campaign) - create campaign and generate briefs
  const handleCreate = async (values) => {
    const { data, briefs_qtt, selectedTagsString } = prepareCampaignData(values);

    const payload = {
      body: data,
    };

    try {
      const result = await createProject(payload);
      onHide();
      renderSuccess(result?.data?.message);
      navigate(
        `/projects/${result?.data?.project?.id}/newPlan?isManual=false`,
        {
          state: {
            project: result?.data?.project,
            campaignCards: true,
            amount: briefs_qtt,
            customAmount: true,
            content_types: selectedTagsString,
          },
        },
      );
    } catch (error) {
      // Error is handled by the showError callback in useCreateProject
    }
  };

  // Main submit handler - routes to appropriate handler based on action
  const handleSubmit = async (values) => {
    if (isEdit) {
      // For edit mode, this is called by "Save & Regenerate" button
      await handleSaveAndRegenerate(values);
    } else {
      // For create mode
      await handleCreate(values);
    }
  };

  return (
    <>
      {isCreateLoading || isUpdateLoading ? (
        <Loader
          inline
          loaderHeading="Saving Project!"
          loaderDescription="Please wait..."
        />
      ) : (
        <Drawer
          show={show}
          onHide={onHide}
          placement="end"
          title={modalTitle}
          width={650}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={manualProjectCreateSchema(isEdit)}
            validateOnMount={true}
            enableReinitialize={true}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty, setFieldValue, values, touched, errors }) => {
              return (
                <Form
                  className={Styles.Drawer_Form}
                  style={{ justifyContent: 'flex-start' }}
                >
                  <div className={Styles.Drawer_Form_InputWrapper}>
                    <div className={Styles.Drawer_Form_InputWrapper_Input}>
                      <FormikTextField
                        name="name"
                        type="string"
                        placeholder="Add campaign title…"
                        Label="Campaign Name"
                        classList="input"
                        isRequired
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="error mt-2"
                      />
                    </div>
                    
                    {/* Brand dropdown now comes before target audience */}
                    <div className={Styles.Drawer_Form_InputWrapper_Input}>
                      <Dropdown
                        name="brand"
                        label="Brand"
                        placeholder="Select Brand"
                        options={transformBrandsData(
                          user?.user_brands?.brands_data || [],
                        )}
                        isClearable
                        value={
                          transformBrandsData(
                            user?.user_brands?.brands_data,
                          ).find((option) => option.value == values.brand) || ''
                        }
                        onChange={async (selectedOptions) => {
                          // Set brand value
                          setFieldValue(
                            'brand',
                            selectedOptions ? selectedOptions.value : '',
                          );
                          
                          // If a brand is selected, fetch its details
                          if (selectedOptions) {
                            try {
                              const brandId = selectedOptions.value;
                              
                              // Show loading state
                              setIsLoadingBrandDetails(true);
                              
                              // Fetch the business details
                              const response = await getBusinessById(brandId);
                              
                              if (response && response.data) {
                                console.log('Business details:', response.data);
                                
                                // Get the summary from either auto-generated or user-entered
                                const summary = response.data.auto_generated_summary || response.data.user_entered_summary;
                                
                                if (summary && summary.target_audience && summary.target_audience.length > 0) {
                                  // Format the target audience as a comma-separated string
                                  const audienceString = summary.target_audience.join(', ');
                                  console.log('Setting target audience:', audienceString);
                                  setFieldValue('target_audience', audienceString);
                                }
                              }
                            } catch (error) {
                              console.error('Error fetching brand details:', error);
                            } finally {
                              setIsLoadingBrandDetails(false);
                            }
                          } else {
                            // Clear target audience when no brand is selected
                            setFieldValue('target_audience', '');
                          }
                        }}
                      />
                      {isLoadingBrandDetails && (
                        <div className="text-muted small mt-1">
                          Loading brand details...
                        </div>
                      )}
                    </div>
                    
                    {/* Target audience and campaign goal come after brand */}
                    <div className={`${Styles.Drawer_Form_InputWrapper_Dates} w-100`}>
                      <div className={Styles.Drawer_Form_InputWrapper_Input}>
                        <FormikTextField
                          name="target_audience"
                          type="string"
                          placeholder="Ex: College students"
                          Label="Target Audience"
                          classList="input form-control"
                        />
                        <ErrorMessage
                          name="target_audience"
                          component="div"
                          className="error mt-2"
                        />
                      </div>
                      <div className={Styles.Drawer_Form_InputWrapper_Input}>
                        <Dropdown
                          name="campaign_goal"
                          label="Campaign Goals"
                          placeholder="Select Campaign Goal"
                          options={CAMPAIGN_GOALS.map((option) => ({
                            label: option.label,
                            value: option.value,
                          }))}
                          value={
                            values.campaign_goal
                              ? CAMPAIGN_GOALS.find(
                                  (option) =>
                                    option.value === values.campaign_goal,
                                )
                              : null
                          }
                          defaultValue={initialValues.campaign_goal}
                          onChange={(option) =>
                            setFieldValue('campaign_goal', option.value)
                          }
                        />
                      </div>
                    </div>
                    
                    <div className={Styles.Drawer_Form_InputWrapper_Input}>
                      <FormikTextField
                        id="description"
                        name="description"
                        type="text"
                        placeholder={`Generate a campaign for the "Vocable Product Launch" to introduce our AI-powered content marketing software by driving brand awareness through organic content. We will highlight how our software solves common challenges like time-consuming content creation and difficulty in maintaining consistency, positioning it as an essential tool for modern marketers.`}
                        Label="Campaign Description"
                        classList="input form-control"
                        isRequired
                        isTextArea
                        onChange={(e) => {
                          // Get the raw text input
                          const rawText = e.target.value;
                          
                          // Clean any existing language instructions before saving to form
                          const cleanedText = removeLanguageInstructions(rawText);
                          setFieldValue('description', cleanedText);
                          
                          // Detect language from the raw input and update the language field
                          const detectedLanguage = detectLanguageFromDescription(rawText);
                          setFieldValue('language', detectedLanguage);
                        }}
                      />
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="error mt-2"
                      />
                    </div>
                    
                    {/* Add Language Dropdown */}
                    <div className={Styles.Drawer_Form_InputWrapper_Input}>
                      <Dropdown
                        name="language"
                        label="Content Language"
                        placeholder="Select Language"
                        options={LANGUAGE_OPTIONS}
                        value={
                          LANGUAGE_OPTIONS.find(
                            (option) => option.value === values.language
                          ) || LANGUAGE_OPTIONS[0]
                        }
                        onChange={(selectedOption) => {
                          const languageValue = selectedOption ? selectedOption.value : 'en-US';
                          setFieldValue('language', languageValue);
                        }}
                        defaultValue={LANGUAGE_OPTIONS[0]}
                      />
                      <div className="form-text text-muted small mt-1">
                        Language is auto-detected from your campaign description
                      </div>
                    </div>
                    <div className={Styles.Drawer_Form_InputWrapper_Input}>
                      <FormikTextField
                        id="key_message"
                        name="key_message"
                        type="text"
                        placeholder="Write key messaging"
                        Label="Key Messaging"
                        classList="input form-control"
                        rows={2}
                        isTextArea
                      />
                    </div>
                    {/* Supporting Documents Upload */}
                    <div className={Styles.Drawer_Form_InputWrapper_Input}>
                      <SupportingDocumentsUpload
                        files={supportingFiles}
                        setFiles={setSupportingFiles}
                        extractedContent={extractedSupportingContent}
                        isExtracting={isExtractingContent}
                        extractionProgress={extractionProgress}
                        errors={supportingDocsErrors}
                        setErrors={setSupportingDocsErrors}
                      />
                    </div>
                    <div
                      className={`${Styles.Drawer_Form_InputWrapper_Dates} w-100`}
                    >
                      <div className="w-100">
                        <FormikDateField
                          name="start_date"
                          label="Start Date"
                          isRequired
                        />
                      </div>
                      <div className="w-100">
                        <FormikDateField
                          name="end_date"
                          label="End Date"
                          isRequired
                        />
                      </div>
                    </div>
                    <div className={Styles.form4_wrapper__tags_container}>
                      <label htmlFor="" className="Input_label form-label">
                        Content Type <span className="text-danger"> *</span>
                      </label>
                      <div
                        className={Styles.form4_wrapper__tags_container__outer}
                      >
                        {tagState?.map((tag) => (
                          <Tag
                            key={tag.id}
                            tag={tag}
                            setTags={setTagState}
                            tags={tagState}
                            Tagclasses={
                              Styles.form4_wrapper__tags_container__outer__tag
                            }
                            iconLeft={tag.icon}
                            selected={tag.selected}
                            callClickHandler={true}
                            onClick={() => {
                              const newTagState = tagState.map((t) =>
                                t.id === tag.id
                                  ? { ...t, selected: !t.selected }
                                  : t,
                              );
                              setTagState(newTagState);
                              setFieldValue(
                                'content_types',
                                newTagState
                                  .filter((t) => t.selected)
                                  .map((t) => t.title),
                              );
                            }}
                          />
                        ))}
                      </div>
                      {touched.content_types && errors.content_types ? (
                        <div className="error mt-2">{errors.content_types}</div>
                      ) : null}
                    </div>
                    <div className={Styles.Drawer_Form_InputWrapper_Input}>
                      <FormikTextField
                        id="content_no"
                        name="content_no"
                        type="number"
                        placeholder="Enter No. of contents"
                        Label="No. of Content"
                        classList="input form-control"
                        iconWithLabel={true}
                        infoTooltip={'Please enter a number between 1 and 30.'}
                        inputProps={{ min: 1, max: 30 }}
                      />

                      <ErrorMessage
                        name="content_no"
                        component="div"
                        className="error mt-2"
                      />
                    </div>
                  </div>
                  <div
                    className="d-flex justify-content-end align-self-end align-bottom drawer-footer"
                    style={{ gap: '7px' }}
                  >
                    {action !== 'create' && (
                      <>
                        {/* Cancel button */}
                        <BSButton
                          variant="light"
                          style={{ padding: '10px 16px' }}
                          ButtonText={'Cancel'}
                          classList={'btn button cancel_btn'}
                          onClick={onHide}
                        />
                        {/* Save Only button - just updates campaign fields */}
                        <BSButton
                          variant="light"
                          style={{ padding: '10px 16px' }}
                          ButtonText={'Save Changes'}
                          classList={'btn button cancel_btn'}
                          onClick={() => handleSaveOnly(values)}
                          disabled={!isValid}
                        />
                      </>
                    )}
                    {/* Main action button */}
                    <BSButton
                      variant="dark"
                      style={{ padding: '10px 16px' }}
                      ButtonText={
                        action === 'create'
                          ? creationType === 'self_powered'
                            ? 'Save'
                            : 'Continue'
                          : 'Save & Regenerate'
                      }
                      classList="secondary_btn"
                      type="submit"
                      disabled={!isValid}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Drawer>
      )}

      <CampaignErrorModal
        show={errorModal}
        onHide={() => setErrorModal(false)}
        errorMessage={errorDetails.message}
        errorLink={errorDetails.link}
      />
    </>
  );
};

export default ProjectDrawer;